import { BASE_URL } from "@/lib/constant";
import { CartItem } from "@/lib/types";
import { create } from "zustand";
import { persist } from "zustand/middleware";

type CartStore = {
  items: CartItem[];
  isLoggedIn: boolean;
  token: string | null;
  loading: boolean;
  error: string | null;
  hydrated: boolean;

  setIsLoggedIn: (value: boolean) => Promise<void>;
  setToken: (token: string | null) => void;

  fetchCart: () => Promise<void>;
  addItem: (item: CartItem) => Promise<void>;
  updateItem: (
    id: string,
    updates: Partial<Omit<CartItem, "id">>,
  ) => Promise<void>;
  removeItem: (id: string) => Promise<void>;
  clearCart: () => void;
};

// Helper to sanitize payload
const toServerPayload = (item: CartItem) => ({
  item_id: item.item_id,
  quantity: item.quantity,
});

export const useCartStore = create<CartStore>()(
  persist(
    (set, get) => ({
      items: [],
      isLoggedIn: false,
      token: null,
      loading: false,
      error: null,
      hydrated: false,

      setToken: (token) => set({ token }),

      setIsLoggedIn: async (value) => {
        set({ isLoggedIn: value });
        if (value) {
          const localItems = get().items;
          const token = get().token;

          // Only sync if there are local items
          if (localItems.length > 0 && token) {
            await Promise.all(
              localItems.map(async (item) => {
                try {
                  const res = await fetch(`${BASE_URL}/cart`, {
                    method: "POST",
                    headers: {
                      "Content-Type": "application/json",
                      Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify(toServerPayload(item)),
                  });

                  if (!res.ok) {
                    console.error(
                      "Failed to sync item:",
                      item,
                      await res.json(),
                    );
                  }
                } catch (err) {
                  console.error("Error syncing item:", item, err);
                }
              }),
            );
          }

          await get().fetchCart();
        } else {
          set({ loading: false });
        }
      },

      fetchCart: async () => {
        const { isLoggedIn, token } = get();
        if (!isLoggedIn || !token) return;

        set({ loading: true, error: null });
        try {
          const res = await fetch(`${BASE_URL}/cart`, {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          });

          if (!res.ok) throw new Error("Failed to fetch cart");
          const data: CartItem[] = await res.json();
          set({ items: data });
        } catch (err: unknown) {
          const errorMessage =
            err instanceof Error ? err.message : "Failed to load cart";
          set({ error: errorMessage });
        } finally {
          set({ loading: false });
        }
      },

      addItem: async (item) => {
        const { isLoggedIn, token } = get();

        if (isLoggedIn && token) {
          try {
            const res = await fetch(`${BASE_URL}/cart`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify(toServerPayload(item)),
            });

            if (!res.ok) {
              throw new Error(await res.text());
            }

            await get().fetchCart();
          } catch (err: unknown) {
            const errorMessage =
              err instanceof Error ? err.message : "Failed to load cart";
            set({ error: errorMessage });
          } finally {
            set({ loading: false });
          }
        } else {
          // Guest logic
          set((state) => {
            const existing = state.items.find(
              (i) => i.item_id === item.item_id,
            );

            if (existing) {
              return {
                items: state.items.map((i) =>
                  i.item_id === item.item_id
                    ? { ...i, quantity: i.quantity + item.quantity }
                    : i,
                ),
              };
            }

            return { items: [...state.items, item] };
          });
        }
      },

      updateItem: async (id, updates) => {
        const { isLoggedIn, token } = get();

        set((state) => ({
          items: state.items.map((item) =>
            item?.item_id === Number(id) ? { ...item, ...updates } : item,
          ),
        }));

        if (isLoggedIn && token) {
          try {
            const res = await fetch(`${BASE_URL}/cart/${id}`, {
              method: "PUT",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify(updates),
            });

            if (!res.ok) {
              throw new Error(await res.text());
            }

            await get().fetchCart();
          } catch (err: unknown) {
            const errorMessage =
              err instanceof Error ? err.message : "Failed to load cart";
            set({ error: errorMessage });
          } finally {
            set({ loading: false });
          }
        }
      },

      removeItem: async (id) => {
        const { isLoggedIn, token } = get();

        if (isLoggedIn && token) {
          try {
            const res = await fetch(`${BASE_URL}/cart/${id}`, {
              method: "DELETE",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
            });

            if (!res.ok) {
              throw new Error(await res.text());
            }

            await get().fetchCart();
          } catch (err: unknown) {
            const errorMessage =
              err instanceof Error ? err.message : "Failed to load cart";
            set({ error: errorMessage });
          } finally {
            set({ loading: false });
          }
        } else {
          set((state) => ({
            items: state.items.filter((item) => item.item_id !== Number(id)),
          }));
        }
      },

      clearCart: () => {
        set({ items: [] });
      },
    }),
    {
      name: "cart-storage",
      partialize: (state) => ({
        items: state.items,
      }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.hydrated = true;
        }
      },
    },
  ),
);
